import {Component, DestroyRef, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NavigationStart, Router} from '@angular/router';
import {defer, filter, finalize, Observable, Subscription, take} from 'rxjs';
import {HOVER_PREVIEW_OPEN_DELAY_MS} from '../../hover-preview.constants';
import {PhotoGalleryKind} from '../sm-photo-gallery/photo-gallery-kind';
import {PhotoGalleryService} from '../sm-photo-gallery/photo-gallery.service';
import {Injector} from '@angular/core';

const TOUCH_PREVIEW_HOLD_DELAY_MS = 500;
const TOUCH_PREVIEW_MOVE_TOLERANCE_PX = 8;

@Component({
  selector: 'sm-image-preview',
  templateUrl: './sm-image-preview.component.html',
  styleUrls: ['./sm-image-preview.component.scss'],
  standalone: false,
})
export class SmImagePreviewComponent implements OnChanges, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly injector = inject(Injector);
  private openTimer: number | null = null;
  private touchHoldTimer: number | null = null;
  private touchStart: {pointerId: number; x: number; y: number} | null = null;
  private suppressTouchClick = false;
  private galleryRequest?: Subscription;
  private hoverPointer: {x: number; y: number} | null = null;
  private hoverBlocked = false;
  @ViewChild('triggerImage') private triggerImage?: ElementRef<HTMLImageElement>;

  @Input() src = '';
  @Input() alt = '';
  @Input() galleryImages: string[] | null = null;
  @Input() sourceKind: PhotoGalleryKind | null = null;
  @Input() sourceId: string | null = null;
  @Input() loadGalleryImages: (() => Observable<string[]>) | null = null;
  loadedGalleryImages: string[] = [];
  galleryLoading = false;
  galleryLoadFailed = false;
  galleryOpen = false;
  @Input() longPressEnabled = false;
  @Output() imageError = new EventEmitter<void>();

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationStart => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.closePreview();
      });
  }

  get hasImage(): boolean {
    return Boolean(this.src.trim());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['src'] || changes['loadGalleryImages'] || changes['sourceKind'] || changes['sourceId']) this.closePreview();
  }

  handleTriggerMouseEnter(event?: MouseEvent): void {
    if (event) this.hoverPointer = {x: event.clientX, y: event.clientY};
    if (!this.hasImage || this.hoverBlocked) return;
    this.cancelOpenTimer();
    this.openTimer = window.setTimeout(() => {
      this.openTimer = null;
      this.openPreview();
    }, HOVER_PREVIEW_OPEN_DELAY_MS);
  }

  handleTriggerMouseLeave(event?: MouseEvent): void {
    this.cancelOpenTimer();
    if (event) this.hoverPointer = {x: event.clientX, y: event.clientY};
    if (!this.isPointerOverTrigger()) this.hoverBlocked = false;
  }

  @HostListener('document:mousemove', ['$event'])
  handleMouseMove(event: MouseEvent): void {
    if (!this.galleryOpen && !this.hoverBlocked) return;
    this.hoverPointer = {x: event.clientX, y: event.clientY};
    if (!this.isPointerOverTrigger()) this.hoverBlocked = false;
  }

  private isPointerOverTrigger(): boolean {
    const rect = this.triggerImage?.nativeElement.getBoundingClientRect();
    const point = this.hoverPointer;
    return !!rect && !!point && point.x >= rect.left && point.x < rect.right
      && point.y >= rect.top && point.y < rect.bottom;
  }

  handleTriggerClick(event: MouseEvent): void {
    if (this.suppressTouchClick) {
      event.preventDefault();
      event.stopPropagation();
      this.suppressTouchClick = false;
      return;
    }
    if (this.galleryOpen) {
      event.preventDefault();
      event.stopPropagation();
      this.closePreview();
    }
  }

  closePreview(): void {
    if (this.galleryOpen) this.hoverBlocked = this.isPointerOverTrigger();
    this.galleryOpen = false;
    this.galleryRequest?.unsubscribe();
    this.galleryRequest = undefined;
    this.loadedGalleryImages = [];
    this.galleryLoading = false;
    this.galleryLoadFailed = false;
    this.cancelOpenTimer();
    this.cancelTouchHold();
  }

  handleTriggerPointerDown(event: PointerEvent): void {
    if (!this.longPressEnabled || event.pointerType !== 'touch') return;
    this.cancelTouchHold();
    if (!this.hasImage) return;
    this.touchStart = {pointerId: event.pointerId, x: event.clientX, y: event.clientY};
    this.touchHoldTimer = window.setTimeout(() => {
      this.touchHoldTimer = null;
      this.suppressTouchClick = true;
      this.openPreview();
    }, TOUCH_PREVIEW_HOLD_DELAY_MS);
  }

  handleTriggerPointerMove(event: PointerEvent): void {
    if (!this.touchStart || this.touchStart.pointerId !== event.pointerId) return;
    if (Math.abs(event.clientX - this.touchStart.x) > TOUCH_PREVIEW_MOVE_TOLERANCE_PX
      || Math.abs(event.clientY - this.touchStart.y) > TOUCH_PREVIEW_MOVE_TOLERANCE_PX) {
      this.cancelTouchHold();
    }
  }

  handleTriggerPointerEnd(event: PointerEvent): void {
    this.cancelTouchHold();
    if (this.galleryOpen && event.pointerType === 'touch') {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  @HostListener('window:scroll')
  handleWindowScroll(): void {
    this.closePreview();
  }

  ngOnDestroy(): void {
    this.closePreview();
  }

  private openPreview(): void {
    if (this.galleryOpen) return;
    if (this.sourceKind && this.sourceId) {
      const kind = this.sourceKind, id = this.sourceId;
      this.loadPreview(() => this.injector.get(PhotoGalleryService).load(kind, id));
      return;
    }
    if (this.loadGalleryImages) {
      this.loadPreview(this.loadGalleryImages);
      return;
    }
    this.galleryOpen = this.hasImage;
  }

  private loadPreview(loader: () => Observable<string[]>): void {
    this.galleryOpen = true;
    this.galleryLoading = true;
    this.galleryLoadFailed = false;
    this.loadedGalleryImages = [];
    this.galleryRequest = defer(loader).pipe(
      take(1),
      finalize(() => this.galleryLoading = false),
    ).subscribe({
      next: images => this.loadedGalleryImages = images,
      error: () => this.galleryLoadFailed = true,
    });
  }

  private cancelOpenTimer(): void {
    if (this.openTimer === null) {
      return;
    }

    window.clearTimeout(this.openTimer);
    this.openTimer = null;
  }

  private cancelTouchHold(): void {
    this.touchStart = null;
    if (this.touchHoldTimer === null) return;
    window.clearTimeout(this.touchHoldTimer);
    this.touchHoldTimer = null;
  }
}
